import { API_CALL } from '../../../../middleware/api';
import { actions as orgFilterActions } from '../../../../components/OrgSelect/orgFilterActions';

// Redux actions for the superuser "ACCESS simulator" tab
//
// Endpoint convention: every action spells out the `portal/` prefix, exactly like
// pages/SuperUser/ExternalApi/actions.js does — the API middleware only prepends
// the environment base URL (constants.getBaseUrl()), not `/portal`.

const BASE = 'portal/dev/access-simulator';

export const GET_ACCESS_SIM_CONFIG = 'GET_ACCESS_SIM_CONFIG';
export const GET_ACCESS_SIM_CONFIG_RESULT = `${GET_ACCESS_SIM_CONFIG}/result`;

export const POINT_ACCESS_SIM_ORG = 'POINT_ACCESS_SIM_ORG';
export const POINT_ACCESS_SIM_ORG_RESULT = `${POINT_ACCESS_SIM_ORG}/result`;

export const UNPOINT_ACCESS_SIM_ORG = 'UNPOINT_ACCESS_SIM_ORG';
export const UNPOINT_ACCESS_SIM_ORG_RESULT = `${UNPOINT_ACCESS_SIM_ORG}/result`;

export const LIST_ACCESS_SIM_PATIENTS = 'LIST_ACCESS_SIM_PATIENTS';
export const LIST_ACCESS_SIM_PATIENTS_RESULT = `${LIST_ACCESS_SIM_PATIENTS}/result`;

export const LIST_ACCESS_SIM_ORG_PATIENTS = 'LIST_ACCESS_SIM_ORG_PATIENTS';
export const LIST_ACCESS_SIM_ORG_PATIENTS_RESULT = `${LIST_ACCESS_SIM_ORG_PATIENTS}/result`;

export const CREATE_ACCESS_SIM_PATIENT = 'CREATE_ACCESS_SIM_PATIENT';
export const CREATE_ACCESS_SIM_PATIENT_RESULT = `${CREATE_ACCESS_SIM_PATIENT}/result`;

export const UPDATE_ACCESS_SIM_PATIENT = 'UPDATE_ACCESS_SIM_PATIENT';
export const UPDATE_ACCESS_SIM_PATIENT_RESULT = `${UPDATE_ACCESS_SIM_PATIENT}/result`;

export const DELETE_ACCESS_SIM_PATIENT = 'DELETE_ACCESS_SIM_PATIENT';
export const DELETE_ACCESS_SIM_PATIENT_RESULT = `${DELETE_ACCESS_SIM_PATIENT}/result`;

export const GET_ACCESS_SIM_COMM_LOG = 'GET_ACCESS_SIM_COMM_LOG';
export const GET_ACCESS_SIM_COMM_LOG_RESULT = `${GET_ACCESS_SIM_COMM_LOG}/result`;

export const SET_ACCESS_SIM_PATIENT_SCRIPT = 'SET_ACCESS_SIM_PATIENT_SCRIPT';
export const SET_ACCESS_SIM_PATIENT_SCRIPT_RESULT = `${SET_ACCESS_SIM_PATIENT_SCRIPT}/result`;

export const CLEAR_ACCESS_SIM_PATIENT_SCRIPT = 'CLEAR_ACCESS_SIM_PATIENT_SCRIPT';
export const CLEAR_ACCESS_SIM_PATIENT_SCRIPT_RESULT = `${CLEAR_ACCESS_SIM_PATIENT_SCRIPT}/result`;

export const SET_ACCESS_SIM_ORG_DEFAULT = 'SET_ACCESS_SIM_ORG_DEFAULT';
export const SET_ACCESS_SIM_ORG_DEFAULT_RESULT = `${SET_ACCESS_SIM_ORG_DEFAULT}/result`;

export const RESET_ACCESS_SIM = 'RESET_ACCESS_SIM';
export const RESET_ACCESS_SIM_RESULT = `${RESET_ACCESS_SIM}/result`;

export const SEND_ACCESS_SIM_UNALIGNMENT_EMAIL = 'SEND_ACCESS_SIM_UNALIGNMENT_EMAIL';
export const SEND_ACCESS_SIM_UNALIGNMENT_EMAIL_RESULT = `${SEND_ACCESS_SIM_UNALIGNMENT_EMAIL}/result`;

// A superuser usually has no organization of his own, so every call carries the
// org the tab is currently pointed at (`org_uuid`: a query parameter on GET /
// DELETE, a body field on PUT / POST). Left out when the caller has an org and
// the backend resolves it from the session.
const withOrg = (orgUUID, params) => (orgUUID ? { ...params, org_uuid: orgUUID } : params);

export const actions = {
  // Reused as-is: returns the orgs the superuser can act on, with `masked_id`
  // being the org UUID the simulator endpoints expect.
  getMyOrgs: orgFilterActions.getMyOrgs,

  getConfig: orgUUID => ({
    [API_CALL]: { method: 'GET', endpoint: `${BASE}/config`, params: withOrg(orgUUID) },
    type: GET_ACCESS_SIM_CONFIG,
  }),

  pointOrg: (body, orgUUID) => ({
    [API_CALL]: { method: 'PUT', endpoint: `${BASE}/point-org`, data: { ...(body || {}), ...withOrg(orgUUID) } },
    type: POINT_ACCESS_SIM_ORG,
  }),

  unpointOrg: orgUUID => ({
    [API_CALL]: { method: 'PUT', endpoint: `${BASE}/unpoint-org`, params: withOrg(orgUUID) },
    type: UNPOINT_ACCESS_SIM_ORG,
  }),

  listPatients: orgUUID => ({
    [API_CALL]: { method: 'GET', endpoint: `${BASE}/patients`, params: withOrg(orgUUID) },
    type: LIST_ACCESS_SIM_PATIENTS,
  }),

  listOrgPatients: orgUUID => ({
    [API_CALL]: { method: 'GET', endpoint: `${BASE}/org-patients`, params: withOrg(orgUUID) },
    type: LIST_ACCESS_SIM_ORG_PATIENTS,
  }),

  createPatient: (patient, orgUUID) => ({
    [API_CALL]: { method: 'POST', endpoint: `${BASE}/patients`, data: { ...patient, ...withOrg(orgUUID) } },
    type: CREATE_ACCESS_SIM_PATIENT,
  }),

  updatePatient: (id, patient, orgUUID) => ({
    [API_CALL]: { method: 'PUT', endpoint: `${BASE}/patients/${id}`, data: { ...patient, ...withOrg(orgUUID) } },
    type: UPDATE_ACCESS_SIM_PATIENT,
  }),

  deletePatient: (id, orgUUID) => ({
    [API_CALL]: { method: 'DELETE', endpoint: `${BASE}/patients/${id}`, params: withOrg(orgUUID) },
    type: DELETE_ACCESS_SIM_PATIENT,
  }),

  getCommLog: (id, limit, orgUUID) => ({
    [API_CALL]: { method: 'GET', endpoint: `${BASE}/patients/${id}/communication`, params: withOrg(orgUUID, { limit }) },
    type: GET_ACCESS_SIM_COMM_LOG,
  }),

  setPatientScript: (id, operation, script, orgUUID) => ({
    [API_CALL]: { method: 'PUT', endpoint: `${BASE}/patients/${id}/responses/${operation}`, data: { ...script, ...withOrg(orgUUID) } },
    type: SET_ACCESS_SIM_PATIENT_SCRIPT,
  }),

  clearPatientScript: (id, operation, seq, orgUUID) => ({
    [API_CALL]: { method: 'DELETE', endpoint: `${BASE}/patients/${id}/responses/${operation}`, params: withOrg(orgUUID, { seq }) },
    type: CLEAR_ACCESS_SIM_PATIENT_SCRIPT,
  }),

  setOrgDefault: (script, orgUUID) => ({
    [API_CALL]: { method: 'PUT', endpoint: `${BASE}/defaults`, data: { ...script, ...withOrg(orgUUID) } },
    type: SET_ACCESS_SIM_ORG_DEFAULT,
  }),

  reset: orgUUID => ({
    [API_CALL]: { method: 'POST', endpoint: `${BASE}/reset`, params: withOrg(orgUUID) },
    type: RESET_ACCESS_SIM,
  }),

  sendUnalignmentEmail: (body, orgUUID) => ({
    [API_CALL]: { method: 'POST', endpoint: `${BASE}/unalignment-email`, data: { ...(body || {}), ...withOrg(orgUUID) } },
    type: SEND_ACCESS_SIM_UNALIGNMENT_EMAIL,
  }),
};

export default actions;
